/* eslint-disable no-console */
import { NOTIFICATIONS, notifications } from '@egw/notifications'

/**
 * Convert args to string
 */
const convertArgsToString = (...args: unknown[]): string => {
  const output: string[] = []
  if (args.length) {
    args.forEach((currentValue) => {
      if (typeof currentValue === 'object') {
        return output.push(JSON.stringify(currentValue))
      }
      return output.push(currentValue as string)
    })
  }
  return output.join('\n')
}

/**
 * Logger
 */
class LoggerSingleton {
  private context?: object = {}

  constructor() {
    if (!instance) {
      instance = this
      // GrapqhQl queries and mutations
      const graphqlQueryHandler = (data: unknown): void => {
        const query = (data as { detail: string }).detail
        this.debug('[GraphQL Query]:', query)
      }
      notifications.on(NOTIFICATIONS.CLIENTS.GRAPHQL.QUERY, graphqlQueryHandler)

      // GrapqhQl Errors
      const graphqlErrorHandler = (data: unknown): void => {
        const error = (data as { detail: string }).detail
        this.error('[GraphQL Error]:', error)
      }
      notifications.on(NOTIFICATIONS.CLIENTS.GRAPHQL.ERROR, graphqlErrorHandler)

      // Deep linking
      const deepLinkHandler = (data: unknown): void => {
        const urlData = (data as { detail: unknown }).detail
        this.debug('[Deep Link]:', urlData)
      }
      notifications.on(NOTIFICATIONS.DEEP_LINKING.APP_URL_OPEN, deepLinkHandler)
    }

    // eslint-disable-next-line no-constructor-return
    return instance
  }

  setContext(context: unknown): void {
    if (this.context) {
      this.context = { ...this.context, context }
    } else {
      this.context = { context }
    }
  }

  debug(...args: unknown[]): void {
    if (this.context && Object.keys(this.context).length > 0) {
      console.debug(...args, this.context)
      notifications.dispatch(NOTIFICATIONS.LOGGER.DEBUG, {
        details: convertArgsToString(...args),
        context: this.context,
      })
    } else {
      console.debug(...args)
      notifications.dispatch(NOTIFICATIONS.LOGGER.DEBUG, { details: convertArgsToString(...args) })
    }
  }

  info(...args: unknown[]): void {
    if (this.context && Object.keys(this.context).length > 0) {
      console.info(...args, this.context)
      notifications.dispatch(NOTIFICATIONS.LOGGER.INFO, {
        details: convertArgsToString(...args),
        context: this.context,
      })
    } else {
      console.info(...args)
      notifications.dispatch(NOTIFICATIONS.LOGGER.INFO, { details: convertArgsToString(...args) })
    }
  }

  warn(...args: unknown[]): void {
    if (this.context && Object.keys(this.context).length > 0) {
      console.warn(...args, this.context)
      notifications.dispatch(NOTIFICATIONS.LOGGER.WARN, {
        details: convertArgsToString(...args),
        context: this.context,
      })
    } else {
      console.warn(...args)
      notifications.dispatch(NOTIFICATIONS.LOGGER.WARN, { details: convertArgsToString(...args) })
    }
  }

  error(...args: unknown[]): void {
    if (this.context && Object.keys(this.context).length > 0) {
      console.error(...args, this.context)
      notifications.dispatch(NOTIFICATIONS.LOGGER.ERROR, {
        details: convertArgsToString(...args),
        context: this.context,
      })
    } else {
      console.error(...args)
      notifications.dispatch(NOTIFICATIONS.LOGGER.ERROR, { details: convertArgsToString(...args) })
    }
  }
}

let instance: LoggerSingleton | null = null

/**
 * Logger singleton class. Use this if you don't need to set prefix.
 */
export const log = new LoggerSingleton()

/**
 * Logger class. Uses logger singleton for all the methods. Can be initialized with prefix, which will be added to all logs.
 */
export class Logger {
  private prefix?: string

  constructor(prefix?: string) {
    this.prefix = prefix
  }

  setContext(context: unknown): void {
    const logger = new LoggerSingleton()
    logger.setContext(context)
  }

  debug(...args: unknown[]): void {
    const logger = new LoggerSingleton()
    if (this.prefix) {
      // eslint-disable-next-line testing-library/no-debugging-utils
      logger.debug(`[${this.prefix}]:`, ...args)
    } else {
      // eslint-disable-next-line testing-library/no-debugging-utils
      logger.debug(...args)
    }
  }

  info(...args: unknown[]): void {
    const logger = new LoggerSingleton()
    if (this.prefix) {
      logger.info(`[${this.prefix}]:`, ...args)
    } else {
      logger.info(...args)
    }
  }

  warn(...args: unknown[]): void {
    const logger = new LoggerSingleton()
    if (this.prefix) {
      logger.warn(`[${this.prefix}]:`, ...args)
    } else {
      logger.warn(...args)
    }
  }

  error(...args: unknown[]): void {
    const logger = new LoggerSingleton()
    if (this.prefix) {
      logger.error(`[${this.prefix}]:`, ...args)
    } else {
      logger.error(...args)
    }
  }
}
